import type { DocumentPatch } from '../core/model'

export interface AgentScenarioStep {
  id: string
  label: string
  narrative: string
  patch?: DocumentPatch
}

export interface AgentScenario {
  id: string
  label: string
  prompt: string
  description: string
  steps: AgentScenarioStep[]
}

export const agentScenarios: AgentScenario[] = [
  {
    id: 'bold-launch',
    label: 'Bolder launch',
    prompt: 'Make the launch page feel bolder and more decisive.',
    description: 'Reframe the hierarchy, color signal, and primary action.',
    steps: [
      {
        id: 'inspect',
        label: 'Inspect hierarchy',
        narrative:
          'I found one dominant hero, two competing actions, and a healthy metric grid. I’ll preserve the information architecture and increase conviction through copy and contrast. ',
      },
      {
        id: 'reframe',
        label: 'Reframe the hero',
        narrative:
          'Updating the headline and supporting copy first so the design and code views reconcile from the same atomic patch. ',
        patch: {
          nodes: {
            headline: { text: 'Turn product bets into momentum.' },
            description: {
              text: 'One focused system to choose the signal, ship the work, and make progress impossible to miss.',
              styles: { maxWidth: '560px' },
            },
          },
        },
      },
      {
        id: 'signal',
        label: 'Strengthen the signal',
        narrative:
          'Applying a warm signal token across every bound surface, then promoting the primary action without detaching component styles. ',
        patch: {
          tokens: { accent: '#ff7657' },
          nodes: {
            primaryAction: {
              styles: {
                background: '#ff7657',
                color: '#151612',
              },
            },
            headline: { styles: { maxWidth: '760px' } },
          },
        },
      },
      {
        id: 'verify',
        label: 'Verify preview',
        narrative:
          'The sandbox accepted the new graph, all stable node keys survived reconciliation, and the update stays inside the 16 ms interaction budget. ',
      },
    ],
  },
  {
    id: 'editorial',
    label: 'Editorial calm',
    prompt: 'Give this page a calmer, editorial product direction.',
    description: 'Tune voice, spacing, and palette without changing structure.',
    steps: [
      {
        id: 'inspect',
        label: 'Read visual rhythm',
        narrative:
          'The existing composition has a strong skeleton. I’ll reduce urgency, widen the reading measure, and keep the three proof points intact. ',
      },
      {
        id: 'voice',
        label: 'Refine the voice',
        narrative:
          'Moving the message from launch energy toward considered product craft while retaining the same semantic nodes. ',
        patch: {
          nodes: {
            eyebrow: { text: 'A QUIETER WAY TO SHIP' },
            headline: { text: 'Clarity for the work that matters.' },
            description: {
              text: 'Northstar gives thoughtful teams one place to frame decisions, follow the signal, and keep meaningful work moving.',
            },
          },
        },
      },
      {
        id: 'palette',
        label: 'Tune palette',
        narrative:
          'Binding a cool blue signal and softer canvas tone through the token layer instead of editing individual leaves. ',
        patch: {
          tokens: {
            accent: '#8bb7ff',
            background: '#f1efe9',
          },
          nodes: {
            headline: {
              styles: {
                fontWeight: 590,
                letterSpacing: '-0.062em',
              },
            },
          },
        },
      },
      {
        id: 'verify',
        label: 'Check continuity',
        narrative:
          'Code, canvas, undo history, and the active remix now point to the same revision. No detached styles were introduced. ',
      },
    ],
  },
  {
    id: 'mobile',
    label: 'Mobile hierarchy',
    prompt: 'Tighten the mobile hierarchy and make the CTA easier to scan.',
    description: 'Improve small-screen density and action clarity.',
    steps: [
      {
        id: 'inspect',
        label: 'Audit small viewport',
        narrative:
          'At 390 px the large headline wraps well, but the copy measure and action labels can become more direct. I’ll keep touch targets above 44 px. ',
      },
      {
        id: 'compress',
        label: 'Compress hierarchy',
        narrative:
          'Reducing the hero’s maximum type scale and tightening the supporting measure for a faster first scan. ',
        patch: {
          nodes: {
            headline: {
              styles: {
                fontSize: 'clamp(48px, 8vw, 82px)',
                maxWidth: '680px',
              },
            },
            description: { styles: { maxWidth: '470px' } },
          },
        },
      },
      {
        id: 'actions',
        label: 'Clarify actions',
        narrative:
          'Shortening both action labels and preserving their stable keys, so event wiring would survive the reconciliation pass. ',
        patch: {
          nodes: {
            primaryAction: { text: 'Start now  ↗' },
            secondaryAction: { text: 'Read field notes' },
          },
        },
      },
      {
        id: 'verify',
        label: 'Verify touch layout',
        narrative:
          'The responsive sandbox reports a single-column metric stack, readable copy, and no horizontal overflow. ',
      },
    ],
  },
]

export function matchScenario(prompt: string): AgentScenario {
  const normalized = prompt.toLowerCase()
  if (normalized.includes('mobile') || normalized.includes('small')) {
    return agentScenarios[2]
  }
  if (
    normalized.includes('calm') ||
    normalized.includes('editorial') ||
    normalized.includes('quiet')
  ) {
    return agentScenarios[1]
  }
  return agentScenarios[0]
}
